/**
 * Supabase client for CheepSweepz Google sign-in.
 * OAuth completes on this site's /auth/callback (direct PKCE) or via DHC hub hash handoff.
 */
import { createClient } from '@supabase/supabase-js';
import { assertBrowserSupabaseAnonKey } from './supabase-key-guard';

const url = process.env.REACT_APP_SUPABASE_URL ?? '';
const anonKey = assertBrowserSupabaseAnonKey(process.env.REACT_APP_SUPABASE_ANON_KEY ?? '') ?? '';

const AUTH_STORAGE_KEY = 'cheepsweepz-supabase-auth';

/** Mirror PKCE verifier to sessionStorage so redirects are less likely to lose it. */
const dualAuthStorage = {
  getItem(key) {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
  },
  setItem(key, value) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
    window.sessionStorage.setItem(key, value);
  },
  removeItem(key) {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  },
};

export const supabase =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          flowType: 'pkce',
          // Only /auth/callback exchanges codes — avoids races on /login?code=...
          detectSessionInUrl: false,
          persistSession: true,
          autoRefreshToken: true,
          storageKey: AUTH_STORAGE_KEY,
          storage: dualAuthStorage,
        },
      })
    : null;

/** Where to send the user after Google sign-in completes on this domain. */
export const AUTH_NEXT_STORAGE_KEY = 'cheepsweepz_auth_next';

export const OAUTH_EXCHANGE_LOCK_KEY = 'cheepsweepz_oauth_exchange_lock';

const HUB_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') ||
  'https://digitalhustlerscaribbean.com';

const SITE_URL =
  (process.env.REACT_APP_SITE_URL || 'https://cheepsweepz.shop').replace(/\/+$/, '');

export function getCheepSweepzAuthCallbackUrl() {
  // OAuth return must stay on cheepsweepz.shop (apex 302 → app preserves ?code=)
  if (typeof window === 'undefined') {
    return `${SITE_URL}/auth/callback`;
  }
  const host = window.location.hostname;
  if (host === 'cheepsweepz.shop' || host === 'www.cheepsweepz.shop' || host === 'app.cheepsweepz.shop') {
    return `${SITE_URL}/auth/callback`;
  }
  return `${window.location.origin}/auth/callback`;
}

/**
 * DHC hub login — server-side PKCE on digitalhustlerscaribbean.com, then hash token
 * handoff back to this site's /auth/callback. Avoids PKCE verifier loss on the SPA.
 */
export function getHubLoginUrl(returnPath = '/submit') {
  const callback = getCheepSweepzAuthCallbackUrl();
  const hubCallback = `${HUB_URL}/login?callbackUrl=${encodeURIComponent(callback)}`;
  if (typeof window !== 'undefined' && returnPath?.startsWith('/')) {
    try {
      window.sessionStorage.setItem(AUTH_NEXT_STORAGE_KEY, returnPath);
    } catch {
      /* private mode */
    }
  }
  return hubCallback;
}

/**
 * OAuth via DHC hub (site_url) when Supabase redirect allow-list only includes HQ.
 */
export function getHubOAuthRedirectUrl(satelliteCallbackUrl) {
  const next = satelliteCallbackUrl || getCheepSweepzAuthCallbackUrl();
  return `${HUB_URL}/auth/callback?next=${encodeURIComponent(next)}`;
}

/** Admin emails — mirrored in backend ADMIN_EMAIL_ALLOWLIST. */
export const ADMIN_EMAIL_ALLOWLIST = [
  'rrkinc.2025@gmail.com',
  'cheepsweepz@gmail.com',
  'romellmapp@gmail.com',
];

/** @deprecated Use ADMIN_EMAIL_ALLOWLIST */
export const PRIMARY_ADMIN_EMAIL = ADMIN_EMAIL_ALLOWLIST[0];

export function isAdminEmail(email) {
  return ADMIN_EMAIL_ALLOWLIST.includes((email ?? '').toLowerCase());
}

/** HQ vault-backed Google OIDC provider (correct client secret). */
export const GOOGLE_OAUTH_PROVIDER = 'custom:google-cheep';

/**
 * Direct Google OAuth on CheepSweepz (PKCE). Redirect stays on this domain.
 * Uses vault-backed custom:google-cheep — do not use DHC HQ /auth/google (built-in google).
 */
export async function handleGoogleSignIn(returnPath = '/submit') {
  if (!supabase) {
    throw new Error(
      'Google sign-in is not configured on this site. Use email/password or contact support.'
    );
  }

  const redirectUrl = getCheepSweepzAuthCallbackUrl();

  if (returnPath?.startsWith('/')) {
    try {
      sessionStorage.setItem(AUTH_NEXT_STORAGE_KEY, returnPath);
    } catch {
      /* private mode */
    }
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: GOOGLE_OAUTH_PROVIDER,
    options: {
      redirectTo: redirectUrl,
      queryParams: {
        access_type: 'offline',
        prompt: 'select_account',
      },
    },
  });

  if (error) {
    console.error('Google OAuth error:', error.message);
    throw error;
  }

  return data;
}

/** Resolve post-login path: admin users land on /admin, others on stored next or /submit. */
export async function resolvePostAuthRedirectPath({
  sessionUser,
  apiRole,
  storedNext,
  searchNext,
}) {
  const userEmail = sessionUser?.email?.toLowerCase() ?? '';
  let isAdmin = apiRole === 'admin' || isAdminEmail(userEmail);

  if (!isAdmin && supabase && sessionUser?.id) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('permission_tier, email')
      .eq('supabase_auth_id', sessionUser.id)
      .maybeSingle();

    if (
      profile?.permission_tier === 'MASTER_ADMIN' ||
      profile?.permission_tier === 'MINOR_ADMIN' ||
      isAdminEmail(profile?.email)
    ) {
      isAdmin = true;
    }
  }

  if (isAdmin) {
    return '/admin';
  }

  const next = searchNext || storedNext;
  if (next && next.startsWith('/')) {
    return next;
  }
  return '/submit';
}
